import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useGetActivityQuery } from '../../api/activity';
import { PAGE_SIZE, STATUS_FILTERS, isStatusValidForDomain } from '../../config/activity-display';
import { groupActivityByDate } from '../../lib/activity-groups';
import { useDebouncedActivitySearch } from '../../lib/activity-search';
import type { ActivityDomain, ActivityStatus } from '../../model/activity';
import ActivityDomainFilters from './ActivityDomainFilters';
import ActivityEmptyState from './ActivityEmptyState';
import ActivityErrorState from './ActivityErrorState';
import ActivityFeed from './ActivityFeed';
import ActivityLoadingState from './ActivityLoadingState';
import ActivityPagination from './ActivityPagination';
import ActivityToolbar from './ActivityToolbar';

interface ActivitySectionProps {
  masterProjectId: number;
}

const ActivitySection = ({ masterProjectId }: ActivitySectionProps): JSX.Element => {
  const [domain, setDomain] = useState<ActivityDomain>('all');
  const [status, setStatus] = useState<ActivityStatus>('active');
  const [offset, setOffset] = useState(0);
  const [searchRaw, setSearchRaw] = useState('');
  const search = useDebouncedActivitySearch(searchRaw, 350);

  useEffect(() => {
    setOffset(0);
  }, [search]);

  const { data, isLoading, isFetching, isError, refetch } = useGetActivityQuery({
    masterProjectId,
    domain,
    status,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const availableStatusFilters = useMemo(
    () => STATUS_FILTERS.filter((filter) => filter.domains.includes(domain)),
    [domain]
  );

  const handleRefresh = (): void => {
    refetch().catch(() => {});
  };

  const handleDomainChange = (next: ActivityDomain): void => {
    setDomain(next);
    if (!isStatusValidForDomain(status, next)) {
      setStatus('active');
    }
    setOffset(0);
  };

  const handleStatusChange = (next: ActivityStatus): void => {
    setStatus(next);
    setOffset(0);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const groups = useMemo(() => (data ? groupActivityByDate(data.items) : []), [data]);

  return (
    <div className="flex flex-col gap-4">
      <ActivityToolbar
        searchRaw={searchRaw}
        status={status}
        statusFilters={availableStatusFilters}
        isFetching={isFetching}
        onSearchChange={setSearchRaw}
        onClearSearch={() => setSearchRaw('')}
        onStatusChange={handleStatusChange}
        onRefresh={handleRefresh}
      />

      <ActivityDomainFilters
        domain={domain}
        counts={data?.counts}
        total={data?.total}
        onDomainChange={handleDomainChange}
      />

      {isLoading ? <ActivityLoadingState /> : null}
      {isError ? <ActivityErrorState onRetry={handleRefresh} /> : null}
      {!isLoading && !isError && data?.items.length === 0 ? (
        <ActivityEmptyState search={search} onClearSearch={() => setSearchRaw('')} />
      ) : null}
      {!isLoading && !isError && data && data.items.length > 0 ? (
        <>
          <ActivityFeed groups={groups} total={data.total} currentPage={currentPage} totalPages={totalPages} />
          <ActivityPagination
            offset={offset}
            pageSize={PAGE_SIZE}
            total={data.total}
            currentPage={currentPage}
            totalPages={totalPages}
            isFetching={isFetching}
            onOffsetChange={setOffset}
          />
        </>
      ) : null}
    </div>
  );
};

export default ActivitySection;
